import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import type { 
  AccountResponse,
  BalanceHistoryResponse,
  PriceResponse,
  StakeBalanceResponse
} from '../types';
import { rateLimiter } from '../services/rateLimiter';

const apiKey = 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const CACHE_DURATION = 60000; // 1 minute cache

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

// Global cache for shared data
const priceCache: { current: CacheItem<number> | null } = { current: null };

// Helper function for delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to safely serialize response data
const safeSerialize = (data: any): any => {
  try {
    const serialized = JSON.stringify(data, (key, value) => {
      if (value === undefined || value === null) {
        return null;
      }
      if (typeof value === 'bigint') {
        return value.toString();
      }
      if (typeof value === 'symbol') {
        return value.toString();
      }
      return value;
    });
    return JSON.parse(serialized);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error during serialization';
    console.error('Error serializing data:', errorMessage);
    return null;
  }
};

// Helper function to check if cache is valid
const isCacheValid = (cache: CacheItem<any> | null) => {
  return cache && (Date.now() - cache.timestamp) < CACHE_DURATION;
};

// Helper function to handle API responses with retry logic
const handleApiResponse = async <T,>(
  makeRequest: () => Promise<any>,
  errorMessage: string,
  defaultValue: T,
  retries = MAX_RETRIES
): Promise<T> => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      if (attempt > 0) {
        await delay(RETRY_DELAY * Math.pow(2, attempt - 1)); // Exponential backoff
      }
      
      const response = await makeRequest();
      const serializedData = safeSerialize(response.data);
      
      if (!serializedData) {
        throw new Error('Failed to serialize response data');
      }
      
      return serializedData;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          // Rate limit hit, wait longer before retry
          await delay(RETRY_DELAY * Math.pow(2, attempt));
          continue;
        }
        
        if (error.code === 'ERR_CANCELED') {
          // Request was canceled, don't retry
          throw error;
        }
      }
      
      if (attempt === retries - 1) {
        console.error(`${errorMessage} (Attempt ${attempt + 1}/${retries}):`, error);
        return defaultValue;
      }
    }
  }
  
  return defaultValue;
};

// Fetch price with caching
const fetchCachedPrice = async (signal?: AbortSignal): Promise<number | null> => {
  if (isCacheValid(priceCache.current)) {
    return priceCache.current.data;
  }

  try {
    await rateLimiter.checkRateLimit();
    const response = await handleApiResponse<PriceResponse>(
      () => axios.get(
        'https://api.taostats.io/api/price/latest/v1?asset=tao',
        {
          headers: {
            'accept': 'application/json',
            'Authorization': apiKey
          },
          signal
        }
      ),
      'Failed to fetch price',
      { pagination: { current_page: 1, per_page: 50, total_items: 0, total_pages: 1, next_page: null, prev_page: null }, data: [] }
    );
    
    if (response.data[0]?.price) {
      const price = parseFloat(response.data[0].price);
      priceCache.current = {
        data: price,
        timestamp: Date.now()
      };
      return price;
    }
  } catch (err) {
    if (axios.isAxiosError(err) && err.code !== 'ERR_CANCELED') {
      console.error('Failed to fetch price:', err);
    }
  }
  return null;
};

export function useWalletData(address: string | undefined) {
  const [accountData, setAccountData] = useState<AccountResponse['data'][0] | null>(null);
  const [balanceHistory, setBalanceHistory] = useState<BalanceHistoryResponse['data']>([]);
  const [stakeBalanceData, setStakeBalanceData] = useState<StakeBalanceResponse['data']>([]);
  const [price, setPrice] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    // Clean up previous controller if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    const fetchAllData = async () => {
      if (!address) return;
      
      setError(null);
      setLoading(true);
      
      try {
        // Fetch price and account data in parallel
        const [price, accountResponse] = await Promise.all([
          fetchCachedPrice(signal),
          handleApiResponse<AccountResponse>(
            () => axios.get(
              `https://api.taostats.io/api/account/latest/v1?address=${address}`,
              {
                headers: {
                  'accept': 'application/json',
                  'Authorization': apiKey
                },
                signal
              }
            ),
            'Failed to fetch account data',
            { pagination: { current_page: 1, per_page: 50, total_items: 0, total_pages: 1, next_page: null, prev_page: null }, data: [] }
          )
        ]);

        if (!isMounted) return;

        if (accountResponse.data.length === 0) {
          throw new Error('No data found for this address');
        }

        setPrice(price);
        setAccountData(accountResponse.data[0]);
        setLastUpdated(new Date());

        // Fetch balance history and stake balance in parallel
        const [balanceHistoryResponse, stakeBalanceResponse] = await Promise.all([
          handleApiResponse<BalanceHistoryResponse>(
            () => axios.get(
              `https://api.taostats.io/api/account/history/v1?address=${address}&limit=7`,
              {
                headers: {
                  'accept': 'application/json',
                  'Authorization': apiKey
                },
                signal
              }
            ),
            'Failed to fetch balance history',
            { pagination: { current_page: 1, per_page: 50, total_items: 0, total_pages: 1, next_page: null, prev_page: null }, data: [] }
          ),
          handleApiResponse<StakeBalanceResponse>(
            () => axios.get(
              `https://api.taostats.io/api/dtao/stake_balance/latest/v1?coldkey=${address}`,
              {
                headers: {
                  'accept': 'application/json',
                  'Authorization': apiKey
                },
                signal
              }
            ),
            'Failed to fetch stake balance',
            { pagination: { current_page: 1, per_page: 50, total_items: 0, total_pages: 1, next_page: null, prev_page: null }, data: [] }
          )
        ]);

        if (!isMounted) return;

        // Sort balance history by timestamp
        const sortedBalanceHistory = [...balanceHistoryResponse.data].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        setBalanceHistory(sortedBalanceHistory);
        setStakeBalanceData(stakeBalanceResponse.data);
        
      } catch (err) {
        if (!isMounted) return;
        
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 429) {
            setError('Rate limit exceeded. Please try again in a few minutes.');
          } else if (err.code === 'ERR_CANCELED') {
            // Request was canceled, do nothing
            return;
          } else {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch wallet data';
            setError(errorMessage);
          }
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Failed to fetch wallet data';
          setError(errorMessage);
        }
        
        setAccountData(null);
        setBalanceHistory([]);
        setStakeBalanceData([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (address) {
      fetchAllData();
    }

    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [address]);

  return {
    accountData,
    balanceHistory,
    stakeBalanceData,
    price,
    loading,
    error,
    lastUpdated
  };
}